import type { ChildIssueFieldKeys } from '@jira/issue__shared-types/src/TypesChildIssue.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	createContainer,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
	type Store,
} from '@atlassian/react-sweet-state';
import { actions } from './setIssueContext.tsx';
import { getChildIssuesLimit, getChildIssuesIssueLimitUrls } from './getChildIssuesLimit.tsx';
import type {
	IssueContext,
	IssueNavigatorData,
	OnIssueKeyChangeFn,
	State,
	StoreApi,
} from './IssueContextServiceActions.types.tsx';
import { getViewModeFromLocalStorage } from './getViewModeFromLocalStorage.tsx';

export type Actions = typeof actions;

export const initialState: IssueContext = {
	childIssuesLimit: undefined,
	childIssuesIssueLimitUrls: undefined,
	issueKey: '',
	isArchived: false,
	isClassifyEditEnabled: false,
	isModal: false,
	maxTokenLength: 0,
	isLoadedWithPage: false,
	rapidViewMap: undefined,
	estimationStatistic: undefined,
	isLoading: false,
	// initialise viewMode with the value stored in local storage
	viewMode: getViewModeFromLocalStorage(),
	analyticsSource: undefined,
	backButtonUrl: undefined,
	issueNavigatorData: undefined,
	boardId: undefined,
	boardType: undefined,
	isSpaEnabled: false,
	loginRedirectUrl: undefined,
	onIssueKeyChange: undefined,
	rapidViewId: undefined,
	viewScreenId: undefined,
	isAiOptInEnabled: false,
	isAiEnabledForIssue: false,
	hasRedactions: false,
	issueHierarchyLevel: undefined,
	issueType: undefined,
} as const;

export const store: Store<
	IssueContext,
	{
		readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
		readonly setIssueContext: (
			issueContext: IssueContext,
		) => ({ setState, getState }: StoreApi) => void;
		readonly mergeIssueContext: (
			partialIssueContext: Partial<IssueContext>,
		) => ({ getState, setState }: StoreApi) => void;
		readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
		readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
		readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
		readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
		readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
		readonly getIssueKey: () => ({ getState }: StoreApi) => string;
		readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
		readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
		readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
		readonly resetDetailView: () => ({ setState }: StoreApi) => void;
	}
> = createStore<State, Actions>({
	name: 'issue-context',
	initialState,
	actions,
});

const getIssueContext = (state: State) => state;
const getIsModal = (state: State) => state.isModal;
const getIssueKey = (state: State) => state.issueKey;
const getIsArchived = (state: State) => state.isArchived;
const getIsClassifyEditEnabled = (state: State) => state.isClassifyEditEnabled;
const getIssueId = (state: State) => state.issueId;
// @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type.
const getAnalyticsSource = (state) =>
	state.analyticsSource !== undefined ? state.analyticsSource : undefined;
const getRapidViewMap = (state: State) => state.rapidViewMap;
const getMaxTokenLength = (state: State) => state.maxTokenLength;
const getIsLoadedWithPage = (state: State) => state.isLoadedWithPage;
const getEstimationStatistic = (state: State) => state.estimationStatistic;
const getIsLoading = (state: State) => state.isLoading;
const getViewMode = (state: State) => state.viewMode;
const getBackButtonUrl = (state: State) => state.backButtonUrl;
const getIssueNavigatorData = (state: State) => state.issueNavigatorData;
const getBoardId = (state: State) => state.boardId;
const getBoardType = (state: State) => state.boardType;
const getIsSpaEnabled = (state: State) => state.isSpaEnabled;
const getLoginRedirectUrl = (state: State) => state.loginRedirectUrl;
const getOnIssueKeyChange = (state: State) => state.onIssueKeyChange;
const getRapidViewId = (state: State) => state.rapidViewId;
const getViewScreenId = (state: State) => state.viewScreenId;
const getIsAiOptInEnabled = (state: State) => Boolean(state.isAiOptInEnabled);
const getisAiEnabledForIssue = (state: State) => Boolean(state.isAiEnabledForIssue);
const getHasRedactions = (state: State) => Boolean(state.hasRedactions);
const getIssueTypeName = (state: State) => state.issueType;
const getIssueHierarchyLevel = (state: State) => state.issueHierarchyLevel;
export const useIssueContextStore: HookFunction<
	IssueContext,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueContext,
});

export const useIssueContextStoreChildIssuesLimit: HookFunction<
	number,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getChildIssuesLimit,
});

export const useIssueContextStoreChildIssuesIssueLimitUrls: HookFunction<
	Record<ChildIssueFieldKeys, string | undefined> | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getChildIssuesIssueLimitUrls,
});

export const useIssueContextStoreIssueKey: HookFunction<
	string,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueKey,
});

export const useIssueContextStoreIssueId: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueId,
});

export const useIssueContextStoreIsArchived: HookFunction<
	boolean | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsArchived,
});

export const useIssueContextStoreIsClassifyEditEnabled: HookFunction<
	boolean | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsClassifyEditEnabled,
});

export const useIssueContextStoreIsModal: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsModal,
});

export const useIssueContextStoreViewScreenId: HookFunction<
	number | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getViewScreenId,
});

export const useIssueContextStoreAnalyticsSource: HookFunction<
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	any,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getAnalyticsSource,
});

export const useIssueContextRapidViewMap: HookFunction<
	string[][] | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getRapidViewMap,
});

export const useIssueContextStoreMaxTokenLength: HookFunction<
	number,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getMaxTokenLength,
});

export const useIssueContextStoreIsLoadedWithPage: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsLoadedWithPage,
});

export const useIssueContextStoreEstimationStatistic: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getEstimationStatistic,
});

export const useIssueContextStoreIsLoading: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsLoading,
});

export const useIssueContextStoreViewMode: HookFunction<
	('MODAL' | 'SIDEBAR' | 'DEFAULT') | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getViewMode,
});

export const useIssueContextStoreBackButtonUrl: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getBackButtonUrl,
});

export const useIssueContextStoreIssueNavigatorData: HookFunction<
	IssueNavigatorData | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueNavigatorData,
});

export const useIssueContextStoreBoardId: HookFunction<
	number | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getBoardId,
});

export const useIssueContextStoreBoardType: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getBoardType,
});

export const useIssueContextStoreIsSpaEnabled: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsSpaEnabled,
});

export const useIssueContextStoreLoginRedirctUrl: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getLoginRedirectUrl,
});

export const useIssueContextStoreOnIssueKeyChange: HookFunction<
	OnIssueKeyChangeFn | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getOnIssueKeyChange,
});

export const useIssueContextStoreRapidViewId: HookFunction<
	number | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getRapidViewId,
});

export const useIssueContextIsAiOptInEnabled: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIsAiOptInEnabled,
});

export const useIssueContextIsAiEnabledForIssue: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getisAiEnabledForIssue,
});

export const useIssueContextHasRedactions: HookFunction<
	boolean,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getHasRedactions,
});

export const useIssueTypeName: HookFunction<
	string | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueTypeName,
});

export const useIssueHierarchyLevel: HookFunction<
	number | undefined,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: getIssueHierarchyLevel,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const IssueContextContainer: OverrideContainerComponent<Record<any, any>> = createContainer<
	State,
	Actions,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	Record<any, any>
>(store);

export const useIssueContextStoreActions: HookFunction<
	IssueContext,
	BoundActions<
		IssueContext,
		{
			readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
			readonly setIssueContext: (
				issueContext: IssueContext,
			) => ({ setState, getState }: StoreApi) => void;
			readonly mergeIssueContext: (
				partialIssueContext: Partial<IssueContext>,
			) => ({ getState, setState }: StoreApi) => void;
			readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
			readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
			readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
			readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
			readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
			readonly getIssueKey: () => ({ getState }: StoreApi) => string;
			readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
			readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
			readonly resetDetailView: () => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(store, {
	selector: null,
});
